import {
  DocumentCategoryWithTagsResponse,
  NoneOrderDocumentClassDto,
  ManageDocumentClassDto,
  ManageTagDto,
} from "apis/data-contracts";
import Button from "components/foundation/Button";
import Divider from "components/foundation/Divider";
import Modal from "components/foundation/Modal/Modal";
import TagPageBoard from "pages/tag/TagPageBoard";
import React, { useState } from "react";
import styled from "styled-components";
import TagEditFormItem from "./TagEditFormItem";
import { useNavigate } from "react-router-dom";
import useTagsMutation from "features/tag/hooks/mutations/useTagsMutation";
import Toast from "components/foundation/Toast";
import DocClassEditFormItem from "./DocClassEditFormItem";
import { DOCUMENT_CATEGORY_UUID } from "config/constant";
import type { TagChangePayload } from "./TagEditFormItem";

type TagChange = TagChangePayload & {
  tagCategoryUuid: string;
};

interface TagDetailEditWebPageProps {
  docCategoryUuid: string;
  data: DocumentCategoryWithTagsResponse;
  documentClasses: NoneOrderDocumentClassDto[];
  disableUnloadTracking: () => void;
}

const TagDetailEditWebPage = ({
  docCategoryUuid,
  data,
  documentClasses: existingDocumentClasses,
  disableUnloadTracking,
}: TagDetailEditWebPageProps) => {
  const [isDuplicateTag, setIsDuplicateTag] = useState(false);
  const [isSuccessToast, setIsSuccessToast] = useState(false);
  const navigate = useNavigate();
  const [tags, setTags] = useState<{
    [key: string]: string[];
  }>(
    data.tagCategories.reduce(
      (acc, cur) => {
        acc[cur.tagCategoryUuid] = cur.tags.map((i) => i.tagName);
        return acc;
      },
      {} as { [key: string]: string[] },
    ),
  );
  const [tagChanges, setTagChanges] = useState<Record<string, TagChange[]>>({});
  const { mutate } = useTagsMutation(docCategoryUuid);

  const handleSave = () => {
    let finalDocumentClasses: ManageDocumentClassDto[] = [];

    if (docCategoryUuid === DOCUMENT_CATEGORY_UUID.ARCHIVES) {
      const existingDocClassMap = new Map(
        existingDocumentClasses.map((doc) => [doc.name, doc]),
      );

      // 변경된 문서 구분 리스트
      finalDocumentClasses = documentClasses.map((docClassName, index) => {
        const existing = existingDocClassMap.get(docClassName);

        return {
          documentClassUuid: existing?.uuid ?? "",
          name: docClassName,
          order: index,
        };
      });
    }

    // 변경된 태그 리스트
    const finalTags = data.tagCategories.reduce<ManageTagDto[]>((acc, cur) => {
      const currentCategoryTags = tags[cur.tagCategoryUuid] || [];

      // 기존 태그 이름 → 태그 객체 맵 (서울 → { uuid, name, ... })
      const existingTagMap = new Map(cur.tags.map((tag) => [tag.tagName, tag]));

      // 이 카테고리에서 발생한 변경 기록
      const changes = tagChanges[cur.tagCategoryUuid] ?? [];

      // rename 된 태그의 "toBe(부산)" → 기존 태그 UUID 매핑
      const renameUuidMap = new Map<string, string>();

      for (const c of changes) {
        if (c.type !== "rename") continue; // 여기서부터 c는 rename 타입으로 좁혀짐

        const origin = existingTagMap.get(c.asIs);
        if (origin) {
          renameUuidMap.set(c.toBe, origin.tagUuid);
        }
      }

      const categoryTags = currentCategoryTags.map((tagName, index) => {
        const tagUuid =
          renameUuidMap.get(tagName) ??
          existingTagMap.get(tagName)?.tagUuid ??
          "";

        return {
          tagCategoryUuid: cur.tagCategoryUuid,
          tagUuid,
          name: tagName,
          order: index,
        };
      });

      return [...acc, ...categoryTags];
    }, []);

    mutate(
      {
        manageDocumentClasses: finalDocumentClasses,
        manageTags: finalTags,
      },
      {
        onSuccess: () => {
          setIsSuccessToast(true);
          disableUnloadTracking();
        },
      },
    );

    navigate(-1);
  };

  const handleCategoryTagChange = (
    tagCategoryUuid: string,
    change: TagChangePayload,
  ) => {
    setTagChanges((prev) => {
      const list = prev[tagCategoryUuid] || [];
      const next = [...list];

      if (change.type === "add") {
        const fullChange: TagChange = {
          type: "add",
          tagCategoryUuid,
          toBe: change.toBe,
        };
        next.push(fullChange);
      } else if (change.type === "delete") {
        // 새로 추가했다가 바로 삭제한 경우 diff 제거 등 필요하면 여기서 처리
        const fullChange: TagChange = {
          type: "delete",
          tagCategoryUuid,
          asIs: change.asIs,
        };
        next.push(fullChange);
      } else {
        // rename
        const fullChange: TagChange = {
          type: "rename",
          tagCategoryUuid,
          asIs: change.asIs,
          toBe: change.toBe,
        };
        next.push(fullChange);
      }

      return {
        ...prev,
        [tagCategoryUuid]: next,
      };
    });
  };

  /* 문서 구분 */
  const [documentClasses, setDocumentClasses] = useState<string[]>(
    existingDocumentClasses.map((docClass) => docClass.name),
  );

  return (
    <>
      <Modal.Root open={isDuplicateTag} onOpenChange={setIsDuplicateTag}>
        <Modal.Body
          title="이미 존재하는 태그입니다."
          onConfirm={() => setIsDuplicateTag(false)}
        />
      </Modal.Root>

      <Toast.Message open={isSuccessToast} onOpenChange={setIsSuccessToast}>
        변경 내용이 저장되었습니다.
      </Toast.Message>

      <TagDetailEditPageLayout>
        <div className="tag-detail__header">
          <h1>{data.documentCategoryName} 태그수정</h1>
          <Button variant="tertiary" size="large" onClick={() => navigate(-1)}>
            뒤로
          </Button>
        </div>

        {docCategoryUuid === DOCUMENT_CATEGORY_UUID.ARCHIVES && (
          <DocClassEditFormItem
            documentClasses={documentClasses}
            setDocClasses={setDocumentClasses}
            onDuplicate={() => setIsDuplicateTag(true)}
          />
        )}

        {data.tagCategories.map((tag) => (
          <TagEditFormItem
            tagDetail={tag}
            onDuplicate={() => setIsDuplicateTag(true)}
            tags={tags}
            setTags={setTags}
            onTagChange={handleCategoryTagChange}
            key={tag.tagCategoryUuid}
          />
        ))}

        <Divider />
        <div>
          <Modal.Root>
            <Modal.Body
              title="변경 내용을 저장하시겠습니까?"
              onConfirm={handleSave}
              onCancel
            ></Modal.Body>
            <Modal.Trigger asChild>
              <Button
                size="large"
                style={{
                  margin: "auto",
                }}
              >
                저장
              </Button>
            </Modal.Trigger>
          </Modal.Root>
        </div>
      </TagDetailEditPageLayout>
    </>
  );
};

export default TagDetailEditWebPage;

const TagDetailEditPageLayout = styled(TagPageBoard)`
  .tag-detail__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
`;
